<template>
  <simple-layout>
    <basic-card style="width: max-content; margin-right: 20px;">
      <template slot="header">{{ actualDefinition.name }} [{{ actualDefinition.reportCurrency }}]</template>
      <toolbox>
        <toolbox-button @press="generate" :help-text="$t('cashflow.generate')">
          <icon :icon="['fal', 'cog']" size="lg"/>
        </toolbox-button>
        <toolbox-button @press="handleExportExcel" :help-text="$t('exportExcel')">
          <icon :icon="['fal',  'file-excel']" size="lg"/>
        </toolbox-button>
        <toolbox-toggle-button v-model="showTurnover">
          {{ $t("cashflow.showTurnovers") }}
        </toolbox-toggle-button>

      </toolbox>
      <form-wrapper :form-title="$t('cashflow.reportParameters')">
        <form-select-input :caption="$t('cashflow.definition.reportCurrency')"
                           :options="currencyOptions" v-model="actualDefinition.reportCurrency"
                           help-item-code="cash-flow-report-currency"/>
        <form-multi-select-input :options="cashEffectOptions" :caption="$t('cashflow.definition.effects')"
                                 v-model="actualDefinition.cashEffects"/>
        <form-select-input :caption="$t('cashflow.definition.denominator')"
                           :options="denominationOptions" v-model="actualDefinition.denominator"
                           help-item-code="cash-flow-report-currency"/>
      </form-wrapper>
      <!--            <collapsible-content start-collapsed>-->
      <!--                <template slot="header">-->
      <!--                    Report details-->
      <!--                </template>-->

      <!--                <div style="padding: 5px; margin: 10px 0px 10px 0px; background-color: rgba(10, 10, 10, 0.1)">-->

      <!--                    <ul>-->
      <!--                        <li v-if="actualDefinition.excludeInternalBalanceMovements">Internal movements are excluded from-->
      <!--                            balance turnovers-->
      <!--                        </li>-->
      <!--                        <li v-if="actualDefinition.projectOperationsToBalances">Cash Events are projected to balances-->
      <!--                        </li>-->
      <!--                        <li v-if="actualDefinition.useBreakDateForProjection">Break date expression <u>{{actualDefinition.breakDateExpression}}</u>-->
      <!--                            is used-->
      <!--                        </li>-->
      <!--                    </ul>-->

      <!--                </div>-->
      <!--            </collapsible-content>-->
      <template v-if="loadingReport">
        <loader message="Loading report" infinite/>
      </template>
      <template v-if="!loadingReport && error != null">
        <error-box :error="error"/>
      </template>

      <table class="cashflow-table" v-if="generatedReport != null">
        <thead>
        <tr v-for="(headerRow, rowIdx) in headerLevels">
          <th v-if="rowIdx == 0" :rowspan="headerLevels + 1" class="date-header">
            {{ $t('date') }}
          </th>
          <th v-for="header in generatedReport.headers[rowIdx]"
              :colspan="rowIdx == generatedReport.headers.length - 1 ? (showTurnover ? 3 : 1) : header.count * (showTurnover ? 3 : 1)"
              :rowspan="header.rowCount">{{ header.name }}
          </th>
          <th v-if="rowIdx == 0" class="sum-header" :rowspan="headerLevels + 1 + (showTurnover ? 1 : 0)">
            {{ $t('cashflow.plannedSum') }}
          </th>
          <th v-for="(header, colIdx) in balanceReport.headers[rowIdx]"
              :colspan="rowIdx == balanceReport.headers.length - 1 ? (showTurnover ? 3 : 1) : header.count * (showTurnover ? 3 : 1)"
              :rowspan="header.rowCount">{{ header.name }}
          </th>
          <th v-if="rowIdx == 0" class="sum-header" :rowspan="headerLevels + 1 + (showTurnover ? 1 : 0)">
            {{ $t('cashflow.cashflowSum') }}
          </th>
          <th v-if="rowIdx == 0" class="sum-header" :rowspan="headerLevels + 1 + (showTurnover ? 1 : 0)">
            {{ $t('cashflow.balancesSum') }}
          </th>
        </tr>
        <tr v-if="showTurnover">
          <template v-for="hdr in generatedReport.headers[generatedReport.headers.length - 1]">
            <th>+</th>
            <th>-</th>
            <th>=</th>
          </template>
          <template v-for="hdr in balanceReport.headers[balanceReport.headers.length - 1]">
            <th>+</th>
            <th>-</th>
            <th>=</th>
          </template>
        </tr>
        </thead>
        <tbody>
        <template v-for="(dummy, idx) in generatedReport.rows.length">
          <tr v-if="idx == splitDateIndex">
            <td :colspan="
                            (drillDownCol) * (showTurnover ? 3 : 1)
                            + ((drillDownCol > generatedReport.rows[0].length) && showTurnover ? -1 : 1)
                            + (showTurnover ? 3 : 1)
                            + ((balanceReport.rows[idx].length + generatedReport.rows[idx].length) - drillDownCol ) * (showTurnover ? 3 : 1) + 3"
                class="split-row">
              {{ actualDefinition.splitCaption }}
            </td>
          </tr>
          <tr v-if="($moment(actualReport.startDate).add(idx, 'days').date() == 1) && (idx < startMonthlyBucketsIndex)">
            <td :colspan="
                            (drillDownCol) * (showTurnover ? 3 : 1)
                            + ((drillDownCol > generatedReport.rows[0].length) && showTurnover ? -1 : 1)
                            + (showTurnover ? 3 : 1)
                            + ((balanceReport.rows[idx].length + generatedReport.rows[idx].length) - drillDownCol ) * (showTurnover ? 3 : 1) + 3"
                class="month-row"
            >
              {{
                $moment(actualReport.startDate).add(idx, 'days').format("MM")
              }}/{{ $moment(actualReport.startDate).add(idx, 'days').format("YYYY") }}
            </td>
          </tr>
          <tr v-if="(idx <= startMonthlyBucketsIndex) || (idx > startMonthlyBucketsIndex && $moment(actualReport.startDate).add(idx + 1, 'days').date() == 1)" :class="{ 'break-date-row': idx == breakDateIndex ,
                                    'actual-date-row': idx == actualDateIndex,
                                     'weekend-date-row': idx != actualDateIndex && [0,6].includes($moment(actualReport.startDate).add(idx, 'days').weekday()) }">
            <td
              :class="{'value-cell date-cell': true, 'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1}">
                <template v-if="idx > startMonthlyBucketsIndex">
                    {{$moment(actualReport.startDate).add(idx, 'days').format("MM.YYYY") }}
                </template>
                <template v-else>
                    {{
                    $formats.formatDate($moment(actualReport.startDate).add('days', idx))
                    }}
                </template>
            </td>
            <template v-if="!showTurnover">
              <td v-for="(cell, colIdx) in generatedReport.rows[idx]" :class="{'value-cell': true,
                                'drill-down-cell-single': cell == drillDownCell,
                                'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }"
                  @click.exact="drillDown(cell, idx, colIdx, 'cash-events')">
                {{
                  $numeral((cell.totalValue / 100) / actualDefinition.denominator).format(numberFormat)
                }}
              </td>
            </template>
            <template v-else>
              <template v-for="(cell, colIdx) in generatedReport.rows[idx]">

                <td :class="{'value-cell': true,
                                'drill-down-cell-left': cell == drillDownCell,
                                'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1}">
                  {{
                    $numeral((cell.creditTotal / 100) / actualDefinition.denominator).format(numberFormat)
                  }}
                </td>

                <td :class="{'value-cell': true,
                                'drill-down-cell-middle': cell == drillDownCell,
                                'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }">
                  {{
                    $numeral((cell.debitTotal / 100) / actualDefinition.denominator).format(numberFormat)
                  }}
                </td>

                <td :class="{'value-cell': true,
                                'drill-down-cell-right': cell == drillDownCell,
                                'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }"
                    @click.exact="drillDown(cell, idx, colIdx, 'cash-events')">
                  {{
                    $numeral((cell.totalValue / 100) / actualDefinition.denominator).format(numberFormat)
                  }}
                </td>


              </template>

            </template>

            <td
              :class="{'value-cell': true, 'sum-cell': true, 'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }">
              <template v-if="sumPlanned[idx]">
                {{
                  $numeral((sumPlanned[idx].sum / 100) / actualDefinition.denominator).format(numberFormat)
                }}
              </template>
              <template v-else>
                {{ $numeral(0).format(numberFormat) }}
              </template>
            </td>
            <!------------------- BALANCES PART ------------------------>
            <template v-if="!showTurnover">
              <td v-for="(cell, colIdx) in balanceReport.rows[idx]" class="value-cell" @click.exact="drillDown(cell, idx,
                        generatedReport.rows[idx].length + colIdx + 1, 'balances')"
                  :class="{'value-cell': true,
                            'drill-down-cell-single': cell == drillDownCell,
                            'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }">
                {{
                  $numeral((cell.totalValue / 100) / actualDefinition.denominator).format(numberFormat)
                }}
              </td>
            </template>
            <template v-else>
              <template v-for="(cell, colIdx) in balanceReport.rows[idx]">
                <td :class="{'value-cell': true,
                                    'drill-down-cell-left': cell == drillDownCell,
                                    'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }">
                  <template v-if="actualDefinition.excludeInternalBalanceMovements">
                    {{
                      $numeral(((cell.totalValueCredit - cell.totalValueInternalCredit) /
                        100) / actualDefinition.denominator).format(numberFormat)
                    }}
                  </template>
                  <template v-else>
                    {{
                      $numeral((cell.totalValueCredit / 100) / actualDefinition.denominator).format(numberFormat)
                    }}
                  </template>


                </td>

                <td :class="{'value-cell': true,
                                    'drill-down-cell-middle': cell == drillDownCell,
                                    'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }">
                  <template v-if="actualDefinition.excludeInternalBalanceMovements">
                    {{
                      $numeral(((cell.totalValueDebit - cell.totalValueInternalDebit) /
                        100) / actualDefinition.denominator).format(numberFormat)
                    }}
                  </template>
                  <template v-else>
                    {{
                      $numeral((cell.totalValueDebit / 100) / actualDefinition.denominator).format(numberFormat)
                    }}
                  </template>

                </td>

                <td :class="{'value-cell': true,
                                    'drill-down-cell-right': cell == drillDownCell,
                                    'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }"
                    @click.exact="drillDown(cell, idx,
                                    generatedReport.rows[idx].length + colIdx + 1, 'balances')">
                  {{
                    $numeral((cell.totalValue / 100) / actualDefinition.denominator).format(numberFormat)
                  }}
                </td>
              </template>
            </template>
            <td
              :class="{'value-cell': true, 'sum-cell': true, 'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }">
              <template v-if="sumBalances[idx]">
                {{
                  $numeral((sumBalances[idx].sum / 100) / actualDefinition.denominator).format(numberFormat)
                }}
              </template>
              <template v-else>
                {{ $numeral(0).format(numberFormat) }}
              </template>
            </td>
            <td
              :class="{'value-cell': true, 'sum-cell': true, 'start-of-month-row': $moment(actualReport.startDate).add(idx, 'days').date() == 1 }">
              <template v-if="sumBalances[idx]">
                {{
                  $numeral((sumBalances[idx].onlyBalancesSum / 100) / actualDefinition.denominator).format(numberFormat)
                }}
              </template>
              <template v-else>
                {{ $numeral(0).format(numberFormat) }}
              </template>
            </td>
          </tr>
          <!------------- DRILL DOWN SECTION ----------------->
          <template v-if="drillDownCell != null && drillDownRow == idx">
            <tr>
              <td :colspan="
                            (drillDownCol) * (showTurnover ? 3 : 1)
                            + ((drillDownCol > generatedReport.rows[0].length) && showTurnover ? -1 : 1)"
                  class="drill-down-left-border">&nbsp;
              </td>
              <td class="drill-down-middle-border" :colspan="showTurnover ? 3 : 1">&nbsp;</td>
              <td
                :colspan="((balanceReport.rows[idx].length + generatedReport.rows[idx].length) - drillDownCol ) * (showTurnover ? 3 : 1) + 2"
                class="drill-down-right-border">&nbsp;
              </td>
            </tr>
            <tr>
              <td
                :colspan="(balanceReport.rows[idx].length + generatedReport.rows[idx].length) * (showTurnover ? 3 : 1) + 5"
                class="drill-down-detail-cell"
              >
                <h1 style="text-decoration: underline; margin-bottom: 18px;">
                  <template v-for="(hdr, hdrIdx) in drillDownHeaders">
                    {{ hdr }}
                    <template v-if="hdrIdx != drillDownHeaders.length - 1"> ></template>
                  </template>
                </h1>
                <!--------------------- drill down account balances ----------------->
                <template v-if="drillDownCellType == 'balances'">
                  <h1>Total balance {{
                      $numeral((drillDownCell.totalValue /
                        100) / actualDefinition.denominator).format(numberFormat)
                    }} consists of:</h1>
                  <data-table
                    :key="drillDownCol + '-' + drillDownRow"
                    :data="drillDownCell.balances"
                    :configuration="drillDownTableBalanceConfig"
                    @focusedChanged="focusAccountBalance"
                    :component-id="cashflow-report-drill-down-balances"
                  >
                    <form-wrapper :columns="3">
                      <form-text-input v-model="accountBalanceItemsQuery.message"
                                       caption="Message"/>
                      <form-text-input v-model="accountBalanceItemsQuery.counterAccount"
                                       caption="Counter account"/>
                      <form-text-input v-model="accountBalanceItemsQuery.variableSymbol"
                                       caption="Variable symbol"/>
                      <form-text-input v-model="accountBalanceItemsQuery.amount"
                                       caption="Amount"/>
                    </form-wrapper>
                    <simple-layout horizontal>
                      <empty/>
                      <empty/>
                      <simple-button @press="filterAccountBalanceItems">Filter</simple-button>
                    </simple-layout>
                    <br/>

                    <data-table v-if="selectedAccountBalances != null"
                                :data="accountBalanceItems(selectedAccountBalances).content"
                                :configuration="accountBalanceItemsTableConfig"
                                :pagination="itemsPagination(selectedAccountBalances)"
                                @pageChanged="itemsPageChanged"
                                component-id="cashflow-report-account-balance-items"
                    />
                  </data-table>
                  <h1>{{ $t("cashflow.drillDownIncludesCashEvent") }}:</h1>
                  <data-table
                    :data="drillDownCell.events"
                    :configuration="drillDownTableEventConfig"
                    component-id="cashflow-report-account-balance-ce-items"
                  />
                </template>
                <!--------------------- drill down cash events ------------------->
                <template v-else>
                  <template v-if="incomeDrillDownEvents.length > 0">
                    <h1>Příjmy</h1>
                    <div class="dd-currency-container">

                      <data-table
                        :data="incomeDrillDownEvents"
                        :configuration="drillDownTableCashEventsConfig"
                        @sortChanged="eventSortChanged"
                        :default-sort="currentSort"
                        @openOperation="handleOpenOperation"
                        @deleteOperation="handleDeleteOperation"
                        component-id="cashflow-report-cash-event-items"
                      />
                    </div>
                    <br/><br/>
                  </template>
                  <template v-if="outcomeDrillDownEvents.length > 0">
                    <h1>Výdaje</h1>
                    <div class="dd-currency-container">

                      <data-table
                        :data="outcomeDrillDownEvents"
                        :configuration="drillDownTableCashEventsConfig"
                        :default-sort="currentSort"
                        @sortChanged="eventSortChanged"
                        @openOperation="handleOpenOperation"
                        @deleteOperation="handleDeleteOperation"
                        component-id="cashflow-report-cash-event-items"
                      />
                    </div>
                  </template>
                  <br/>
                </template>

              </td>
            </tr>
          </template>
        </template>
        </tbody>
      </table>
    </basic-card>
    <v-modal name="operationModal" classes="transparent-modal" style="background-color: rgba(10,10,10,0.66)">
      <operation-editor-layout v-if="focusedOperation != null" :key="focusedOperation.id"
                               :operation="focusedOperation"
                               @cancel="handleCancel"
                               @save="handleSave"
      />
    </v-modal>
  </simple-layout>
</template>

<script>
import { mapGetters } from "vuex";
import BasicCard from "../components/layout/BasicCard";
import DashboardLayout from "../components/layout/DashboardLayout";
import SimpleHorizontalLayout from "../components/layout/SimpleHorizontalLayout";
import Empty from "../components/layout/Empty";
import SimpleButton from "../components/button/SimpleButton";
import Loader from "../components/Loader";
import FormWrapper from "../components/form/FormWrapper";
import FormCheckboxInput from "../components/form/FormCheckboxInput";
import DataTable from "../components/table/DataTable";
import FormTextInput from "../components/form/FormTextInput";
import SimpleLayout from "../components/layout/SimpleLayout";
import ToolboxButton from "../components/toolbox/ToolboxButton";
import Toolbox from "../components/toolbox/Toolbox";
import CollapsibleContent from "../components/CollapsibleContent";
import ErrorBox from "../components/error/ErrorBox";
import Axios from "axios";
import OperationEditorLayout from "@/layout/OperationEditorLayout";
import ToolboxToggleButton from "@/components/toolbox/ToggleToolboxButton";
import FormMultiSelectInput from "@/components/form/FormMultiSelectInput";
import FormSelectInput from "@/components/form/FormSelectInput";
import { excelExport } from "@/utils/excel-utils";
// import FormMultiSelectInput from "../components/form/FormMultiSelectInput";
// import CashflowReportDefinitionCard from "../cards/CashflowReportDefinitionCard";
// import FormSelectInput from "../components/form/FormSelectInput";

export default {
  name: "CashflowReportView",
  components: {
    FormSelectInput,
    FormMultiSelectInput,
    ToolboxToggleButton,
    OperationEditorLayout,
    ErrorBox,
    CollapsibleContent,
    Toolbox,
    ToolboxButton,
    SimpleLayout,
    FormTextInput,
    DataTable,
    FormCheckboxInput,
    FormWrapper, Loader, SimpleButton, Empty, SimpleHorizontalLayout, DashboardLayout, BasicCard
  },
  data: function () {
    return {
      currentSort: {sortField: 'amount', direction: 'DESC'},
      generationTime: new Date().getTime(),
      actualDefinition: {},
      focusedOperation: null,
      error: null,
      reportId: this.$route.params.report,
      breakDateIndex: 0,
      actualDateIndex: 0,
      splitDateIndex: 0,
      startMonthlyBucketsIndex: 0,
      generatedReport: null,
      balanceReport: null,
      loadingReport: false,
      headerLevels: 0,
      drillDownCell: null,
      drillDownRow: null,
      drillDownCol: null,
      drillDownHeaders: null,
      drillDownCellType: null,
      showTurnover: false,
      selectedAccountBalances: null,
      accountBalanceItemsQuery: {},
      resultAccountBalanceItemQuery: {},
      denominationOptions: [
        {name: this.$t("ones"), id: 1},
        {name: this.$t("thousands"), id: 1000},
        {name: this.$t("millions"), id: 1000000}
      ],
      accountBalanceItemsTableConfig: {
        columns: [
          {
            caption: "Message",
            datum: d => d.message
          },
          {
            caption: "Internal",
            datum: d => d.internalTransfer ? this.$t("yes") : this.$t("no")
          },
          {
            caption: "Counter account",
            datum: d => d.counterAccount
          },
          {
            caption: "Variable symbol",
            datum: d => d.variableSymbol
          },
          {
            caption: "Constant symbol",
            datum: d => d.constantSymbol
          },
          {
            caption: "Specific symbol",
            datum: d => d.specificSymbol
          },
          {
            caption: "Amount",
            datum: d => this.$formats.formatAmount(d.amount / 100)
          },
          {
            caption: "Currency",
            datum: d => d.currency
          }

        ]
      },
      drillDownTableCashEventsConfig: {

        columns: [
          {
            caption: "Amount",
            datum: d => this.$formats.formatAmount(d.amount / 100),
            allowSort: true,
            raw: d => d.amount,
            sortField: 'amount',
            sortType: 'number'
          },
          {
            caption: "Currency",
            datum: d => d.currency,
            allowSort: true,
            sortField: 'currency'
          },
          {
            caption: "Account",
            datum: d => this.account(d.account).name,
            allowSort: true,
            sortField: 'account'
          },
          {
            caption: "Operation name",
            datum: d => d.operationName
          },
          {
            caption: "Type",
            datum: d => d.type,
            allowSort: true,
            sortField: 'type'
          },
          {
            caption: "Status",
            datum: d => d.status,
            allowSort: true,
            sortField: 'status'
          },
          {
            caption: "Operation",
            datum: d => d.operation,
            allowSort: true,
            sortField: 'operation'
          }
        ],
        actions: [
          {
            icon: 'external-link-square',
            name: 'openOperation',
            accessLambda: (entry) => true,
            tooltip: "Open operation"
          },
          {
            icon: 'trash',
            name: 'deleteOperation',
            accessLambda: (entry) => true,
            tooltip: "Delete operation"
          }
        ]
      },
      drillDownTableEventConfig: {
        columns: [
          {
            caption: "Amount",
            datum: d => this.$formats.formatAmount(d.amount / 100),
            allowSort: true
          },
          {
            caption: "Currency",
            datum: d => d.currency
          },
          {
            caption: "Status",
            datum: d => d.status
          },
          {
            caption: "Type",
            datum: d => d.type
          },
          {
            caption: "Account",
            datum: d => this.account(d.account).name
          }
        ]
      },
      drillDownTableBalanceConfig: {
        selectionMode: 'single',
        allowEdit: true,
        allowSort: true,
        columns: [
          {
            caption: "Account",
            datum: d => this.account(d.accountId).name
          },
          {
            caption: "End balance",
            datum: d => this.$formats.formatAmount(d.endBalance / 100)
          },
          {
            caption: "Credit",
            datum: d => this.$formats.formatAmount(d.totalCredit / 100)
          },
          {
            caption: "Debit",
            datum: d => this.$formats.formatAmount(d.totalDebit / 100)
          },
          {
            caption: "Currency",
            datum: d => this.account(d.accountId).currency
          }

        ]
      }
    }
  },
  computed: {
    currencyOptions() {
      let result = [...this.currencies];
      return result;
    },
    actualReport() {
      return this.report(this.reportId);
    },
    incomeDrillDownEvents() {
      const events = this.drillDownCell.events.filter(evt => evt.amount > 0).sort((a, b) => a.amount - b.amount);
      console.log("income")
      if (this.currentSort?.sortField) {
        console.log("income2")
        return this.$tableSort(this.currentSort, events, this.drillDownTableCashEventsConfig);
      } else {
        return events;
      }
    },
    outcomeDrillDownEvents() {
      const events = this.drillDownCell.events.filter(evt => evt.amount < 0).sort((a, b) => a.amount - b.amount)
      if (this.currentSort?.sortField) {
        return this.$tableSort(this.currentSort, events, this.drillDownTableCashEventsConfig);
      } else {
        return events;
      }
    },

    ...mapGetters({
      currencies: 'currencies/currenciesOptions',
      report: 'cashflowReports/report',
      account: 'banking/account',
      party: 'parties/party',
      reportDefinition: 'cashflowReportDefinitions/cashflowReportDefinition',
      accountBalanceItems: 'banking/accountBalanceItems',
      itemsPagination: 'banking/accountBalanceItemsPagination',
      cashEffects: "cashEffects/cashEffects",
      cashEventFieldValues: "cashEventFieldDistincts/fieldValues",
      getPreference: "userPreferences/getPreference"
    }),
    numberFormat() {
      const decimalPlaces = this.getPreference('cashFlowDecimalPlaces');
      return `0,0.${'0'.repeat(decimalPlaces)}`;
    },
    cashEffectOptions() {
      return this.cashEffects.map(cashEffect => ({
        name: `${cashEffect.category}/${cashEffect.name}`,
        id: cashEffect.id
      }))
    },
    sumPlanned() {
      return this.generatedReport.rows.map(
        row => ({sum: row.reduce((prev, curr) => prev + curr.totalValue, 0)})
      )
    },
    sumBalances() {
      return this.balanceReport.rows.map(
        row => ({
          sum: row.reduce((prev, curr) => prev + curr.totalValue, 0),
          onlyBalancesSum: row.reduce((prev, curr) => prev + curr.balanceValue, 0)
        })
      )
    }
  },
  watch: {
    showTurnover() {
      this.fixHeaders();
    },
    'actualDefinition.cashEffects': {
      handler(from, to) {

      }
    },
    '$route': {
      handler(from, to) {
        this.reportId = this.$route.params.report;
        this.$set(this, 'actualDefinition', this.$_.cloneDeep(this.reportDefinition(this.reportId)));
        this.actualDefinition.denominator = `${this.actualDefinition.denominator}`;
        this.generatedReport = null;
        this.balanceReport = null;
        this.generate();
      },
      immediate: true
    }
  },
  methods: {
    async handleOpenOperation(entry) {

      this.focusedOperation = (await Axios.get(`operation/api/v1/operations/${entry.operation}`)).data

      this.$modal.show("operationModal");
    },

    async handleDeleteOperation(entry) {
      // const operationDefinitionId  = (await Axios.get(`operation/api/v1/operations/${entry.operation}`)).data.operationDefinition;

      if (confirm(this.$t("operation.deleteOperataionConfirmation"))) {
        try {
          await Axios.delete(`operation/api/v1/operations/${entry.operation}`);
          this.$notify({
            group: 'application',
            title: this.$t("operation.deleteSuccessTitle"),
            text: this.$t("operation.deleteSuccess"),
            type: "ok"
          });
          this.generate();

        } catch (e) {
          this.$notify({
            group: 'application',
            title: this.$t("operation.deleteError"),
            text: this.$t("operation.deleteError"),
            type: "error"
          });
        }
      }
    },
    handleSave(savePayload) {
      const operation = savePayload.operation;
      const operationLinks = savePayload.links;

      return Axios.put(`operation/api/v1/operations/${operation.id}`, operation)
        .then((savedOp) => {

          const linkDefinitions = Object.keys(operationLinks.primaryLinks);
          const currentOperationId = savedOp.id;
          return Promise.all(linkDefinitions.map(linkDef => {
            const targetOperationId = operationLinks.primaryLinks[linkDef];
            return this.$store.dispatch("operationLinks/saveOperationLink", {
              primarySide: targetOperationId,
              secondarySide: currentOperationId,
              linkDefinition: linkDef
            })
          }))
        })
        .then(() => {
          this.$modal.hide("operationModal");
          this.$notify({
            group: 'application',
            title: 'Operation saved',
            text: `Operation ${this.focusedOperation.id ? this.focusedOperation.id : ""} saved`,
            type: "ok"
          });
          this.generate()

        }, (error) => {
          this.$notify({
            group: 'application',
            title: 'Operation not saved',
            text: `Failed to save operation ${this.focusedOperation.id ? this.focusedOperation.id : ""}`,
            type: "error"
          });
        });
    },
    handleCancel() {
      this.$modal.hide("operationModal");
    },
    filterAccountBalanceItems() {
      const numberFields = ['amount'];

      //todo: this needs to be moved to separate component
      let parseNumberQueryField = (text) => {
        let operations = {
          ">": "gt",
          "<": "lt",
          ">=": "gte",
          "<=": "lte",
          "=": "eq",
          "~": "around"
        }

        let isOpSpecified = Object.keys(operations).findIndex(op => text.charAt(0) == op) > -1;

        return {
          value: +(isOpSpecified ? text.substr(1) : text) * 100,
          operator: isOpSpecified ? operations[text.charAt(0)] : 'eq',
          type: 'float'
        }
      };

      this.resultAccountBalanceItemQuery = {parameters: {}}
      Object.keys(this.accountBalanceItemsQuery)
        .filter(key => this.accountBalanceItemsQuery[key] !== null && this.accountBalanceItemsQuery[key] !== '')
        .forEach(key => {
          if (numberFields.includes(key)) {
            this.resultAccountBalanceItemQuery.parameters[key] = parseNumberQueryField(this.accountBalanceItemsQuery[key])
          } else {
            this.resultAccountBalanceItemQuery.parameters[key] = {
              type: 'string',
              operator: 'eq',
              value: this.accountBalanceItemsQuery[key]
            }
          }
        })
      if (Object.keys(this.resultAccountBalanceItemQuery.parameters).length == 0) {
        this.resultAccountBalanceItemQuery = null;
      }
      this.$store.dispatch("banking/refreshBalanceItems", {
        balance: this.selectedAccountBalances,
        page: 0,
        query: this.resultAccountBalanceItemQuery
      })
    },
    itemsPageChanged(page) {
      this.$store.dispatch("banking/refreshBalanceItems", {
        balance: this.selectedAccountBalances,
        page: page,
        query: this.resultAccountBalanceItemQuery
      })
    },
    getHeaders(colIdx) {

      let reduceHeaders = (columnIdx) => (acc, curr) => {
        let res = null;
        let idx = 0;
        for (let i = 0; i < curr.length; i++) {
          if (columnIdx >= idx) {
            res = curr[idx]?.name ?? "N/A"; //fixme
          }
          idx += curr[i].count;
        }
        acc.push(res);
        return acc;

      }
      let generatedCols = this.generatedReport.headers[this.generatedReport.headers.length - 1].length;
      let result = [];
      if (colIdx < generatedCols) {
        result = this.generatedReport.headers.reduce(reduceHeaders(colIdx), []);
      } else {
        result = this.balanceReport.headers.reduce(reduceHeaders(colIdx - generatedCols - 2), []);
      }

      return result;
    },
    focusAccountBalance(selection) {

      if (selection != null)
        this.$store.dispatch("banking/refreshBalanceItems", {balance: selection, page: 0})
          .then(() => this.selectedAccountBalances = selection)
    },
    eventSortChanged(sort) {
      this.currentSort.sortField = sort.sortField;
      this.currentSort.direction = sort.direction;
    },
    accountBalanceSortChanged(sort) {
      console.log(sort);
    },
    async drillDown(selectedCell, rowIdx, colIdx, cellType) {

      if (selectedCell.events?.length) {
        const filter = {
          parameters: {
            id: {
              operator: "in",
              type: "entity_reference",
              value: selectedCell.events.map(e => e.originOperation).join(';'),
              negation: false
            }
          }
        }

        const response = await this.$store.dispatch("operations/fetchOperations", {
          query: filter,
          requestedPage: 0,
          size: 1000
        });

        selectedCell.events.forEach(event => {
          const originOperation = response.list.find(operation => operation.id === event.originOperation);
          event.operationName = originOperation?.name ?? 'N/A'
        })

      }


      if (this.drillDownCell == selectedCell) {
        this.drillDownCell = null;
        return;
      }
      this.drillDownCell = selectedCell;
      this.drillDownRow = rowIdx;
      this.drillDownCol = colIdx;
      this.drillDownCellType = cellType;
      this.drillDownHeaders = this.getHeaders(this.drillDownCol);
    },
    fixHeaders() {
      this.$nextTick(() => {
        let tableHeaderTop = this.$el.querySelector('.cashflow-table thead').getBoundingClientRect().top + 20;
        let ths = this.$el.querySelectorAll('.cashflow-table thead th');

        ths.forEach(th => th.style.top = th.getBoundingClientRect().top - tableHeaderTop + "px")

        let dateCells = this.$el.querySelectorAll('.cashflow-table tbody tr td:first-child');
        dateCells.forEach(th => th.style.left = "-20px")
      });
    },
    generate() {
      this.generationTime = new Date().getTime()
      this.generatedReport = null;
      this.balanceReport = null;
      this.error = null;
      this.drillDownCell = null;
      this.loadingReport = true;
      // this.$store.dispatch("cashflowReports/reloadReport", {reportId: this.reportId})
      this.$store.dispatch("cashflowReports/reloadReportTransient", {reportDefinition: this.actualDefinition})
        .then(
          () => {
            this.$set(this, 'generatedReport', this.transformNode(this.actualReport.root, this.actualDefinition.columns, this.$t('cashflow.plannedOperations')));//this.transformReport(this.report(this.reportId));
            this.$set(this, 'balanceReport', this.transformNode(this.actualReport.balanceRoot, this.actualDefinition.balanceColumns, this.$t('cashflow.accountBalances')));
            this.adjustHeaders();

            this.loadingReport = false;
            this.breakDateIndex = this.$moment(this.actualReport.breakDate).diff(this.actualReport.startDate, 'days')
            this.actualDateIndex = this.$moment().diff(this.actualReport.startDate, 'days')
            this.startMonthlyBucketsIndex = this.$moment(this.actualReport.startMonthlyBuckets).diff(this.actualReport.startDate, 'days')
            if (this.actualReport.splitDate) {
              this.splitDateIndex = this.$moment(this.actualReport.splitDate).diff(this.actualReport.startDate, 'days');
            } else {
              this.splitDateIndex = -1;
            }
            this.fixHeaders();
            this.$nextTick(() => {
              this.$forceUpdate();
            })

          }
        )
        .catch(err => {
          this.loadingReport = false;
          if (err.response) {
            this.error = {
              status: err.response.status,
              header: err.response.data.error,
              detail: err.response.data.message
            }
          }

        })
    },
    adjustHeaders() {
      let lastLine = this.generatedReport.headers[this.generatedReport.headers.length - 1];
      if (lastLine.length == 0) {
        this.generatedReport.headers = [this.generatedReport.headers[0], [{
          count: 1,
          rowCount: 1,
          name: "-"
        }]]
      }

      let diff = this.generatedReport.headers.length - this.balanceReport.headers.length;
      if (diff > 0) {
        this.balanceReport.headers[this.balanceReport.headers.length - 1].forEach(c => c.rowCount = diff + 1);
      } else if (diff < 0) {
        this.generatedReport.headers[this.generatedReport.headers.length - 1].forEach(c => c.rowCount = Math.abs(diff) + 1);
      }
      this.headerLevels = this.generatedReport.headers.length + Math.abs(diff);
    },
    transformNode(rootNode, columns, rootHeaderName) {
      const colTransforms = [(d) => d, ...columns.map(col => {
        if (col === 'cashEvent.account') return (d) => this.account(d).name;
        else if (col === 'cashEvent.counterparty') return (d) => this.party(d) == null ? "N/A" : this.party(d).displayName
        else return (d) => d;
      })];

      let leafNodes = [];
      let headers = new Array(columns.length + 1).fill({}).map(() => []);
      let prepareHeaders = (name, node, level) => {
        let childNodes = Object.keys(node.children);
        let result = {
          name: colTransforms[level](name),
          count: 0,
          rowCount: 1
        };
        if (childNodes.length > 0)
          childNodes.forEach(child => {
            result.count += prepareHeaders(child, node.children[child], level + 1).count;
          })
        else {
          leafNodes.push(result);

          result.values = node.cells;
          result.count = 1;
        }
        headers[level].push(result);
        return result;
      }

      let prepareRows = () => {
        let rows = new Array(leafNodes[0].values.length).fill({});
        rows = rows.map(() => new Array(leafNodes.length));
        leafNodes.forEach((leaf, leafIdx) => {
          leaf.values.forEach((leafCell, cellIdx) => {
            rows[cellIdx][leafIdx] = leafCell;
          })
        })
        return rows;
      }

      //headers = prepareHeaders(report.root);
      prepareHeaders(rootHeaderName, rootNode, 0)
      let rows = prepareRows();

      return {
        headers,
        rows
      }

    },
    handleExportExcel() {


      const createHeadersColumns = () => {

        const mergeHeadersColumns = () => {

          const generateHeaderMatrix = (reportName) => {

            const pushCopyRowToFinalArray = (sourceRow) => {
              const copyRow = () => this.$_.cloneDeep(sourceRow).map(col => {
                col.rowCount = 1
                col.name = ''
                return col
              });

              const pushCount = sourceRow[0].rowCount;

              for (let i = 1; i < pushCount; i++) finalArray.push(copyRow());

            }


            const finalArray = [];

            const sourceReverseCopy = this.$_.cloneDeep(this[reportName].headers).reverse();

            for (const headersRow of sourceReverseCopy) {

              finalArray.push(headersRow);

              if (headersRow[0]?.rowCount > 1) pushCopyRowToFinalArray(headersRow);
            }

            return finalArray.reverse();
          }


          const reportHeaders = generateHeaderMatrix('generatedReport');
          const balanceHeaders = generateHeaderMatrix('balanceReport');


          const headersColumns = [];

          for (const [indexRow, reportRow] of Object.entries(reportHeaders)) {

            const mergedArrayOfColumns = [...reportRow, ...balanceHeaders[+indexRow]];
            headersColumns.push(mergedArrayOfColumns)

          }

          return headersColumns;

        };

        const addOperationsColumnsRow = () => {

          for (const columnsRow of finalRowsOfHeaders) {
            for (const column of columnsRow) {
              if (column.name !== '') column.count *= 3
            }
          }

          const columnsCount = finalRowsOfHeaders[finalRowsOfHeaders.length - 1].length * 3;
          const operationsChars = ['+', '-', '='];

          const createCol = (col, index) => {
            const operationIndex = index % 3;
            return {
              name: operationsChars[operationIndex],
              count: 1
            }
          };

          const operationsArray = new Array(columnsCount).fill(null).map(createCol)

          finalRowsOfHeaders.push(operationsArray);
        };

        const createEmptyCol = colCount => ({name: '', count: colCount})

        const addEmptyColsForMerging = () => {
          for (const [index, columnsRow] of Object.entries(finalRowsOfHeaders)) {
            if (+index !== finalRowsOfHeaders.length - 1) {
              const insertingIndex = this.generatedReport.headers[index].length;
              columnsRow.splice(0, 0, createEmptyCol(1));
              columnsRow.splice(insertingIndex + 1, 0, createEmptyCol(1));
            }
          }
        }

        const addSpecialColumns = () => {
          const headersCount = finalRowsOfHeaders.length;

          const createSpecialColumn = (caption) => ({
            name: caption,
            count: 1,
            rowCount: headersCount,
            highlight: true
          })

          const insertingIndex = this.generatedReport.headers[this.generatedReport.headers.length - 1].length * (this.showTurnover ? 3 : 1);

          finalRowsOfHeaders[finalRowsOfHeaders.length - 1].splice(insertingIndex, 0, createSpecialColumn(this.$t('cashflow.plannedSum')));

          finalRowsOfHeaders[finalRowsOfHeaders.length - 1].splice(0, 0, createSpecialColumn(this.$t('date')));

          finalRowsOfHeaders[finalRowsOfHeaders.length - 1].push(createSpecialColumn(this.$t('cashflow.cashflowSum')));

          finalRowsOfHeaders[finalRowsOfHeaders.length - 1].push(createSpecialColumn(this.$t('cashflow.balancesSum')));
        }


        const finalRowsOfHeaders = mergeHeadersColumns();

        if (this.showTurnover) {
          addOperationsColumnsRow();
        }

        addEmptyColsForMerging();

        addSpecialColumns();

        return finalRowsOfHeaders
      }


      const createExportedData = () => {

        const getValueWithDenominator = (value) => ((value / 100) / this.actualDefinition.denominator)

        const NUMBER_SPECIAL_COLS = 4;

        const emptyRowLength = (this.showTurnover ? 3 : 1) * (this.generatedReport.rows[0].length + this.balanceReport.rows[0].length) + NUMBER_SPECIAL_COLS;

        const finalArray = new Array(this.generatedReport.rows.length).fill(null).map(() => new Array(emptyRowLength).fill(null))


        let lastReportColIndex = 0;
        for (const [rowIndex, sourceRow] of Object.entries(this.generatedReport.rows)) {

          const destinationRow = finalArray[+rowIndex];

          destinationRow[0] = this.$formats.formatDate(this.$moment(this.actualReport.startDate).add(+rowIndex, 'days'));

          for (const [colIndex, sourceCol] of Object.entries(sourceRow)) {
            if (this.showTurnover) {
              destinationRow[+colIndex * 3 + 1] = getValueWithDenominator(sourceCol.creditTotal);
              destinationRow[+colIndex * 3 + 2] = getValueWithDenominator(sourceCol.debitTotal);
              destinationRow[+colIndex * 3 + 3] = getValueWithDenominator(sourceCol.totalValue);
            } else {
              destinationRow[+colIndex + 1] = getValueWithDenominator(sourceCol.totalValue);

            }
            lastReportColIndex = +colIndex;
          }

          if (this.showTurnover)
            destinationRow[(lastReportColIndex * 3) + 4] = getValueWithDenominator(this.sumPlanned[+rowIndex].sum);
          else
            destinationRow[lastReportColIndex + 2] = getValueWithDenominator(this.sumPlanned[+rowIndex].sum);


          const balancedRow = this.balanceReport.rows[rowIndex];

          let actualColIndex = 0;

          for (const [colIndex, sourceCol] of Object.entries(balancedRow)) {


            if (this.showTurnover) {

              actualColIndex = ((lastReportColIndex + 1) * 3) + 2 + (+colIndex * 3) - 1;

              if (this.actualDefinition.excludeInternalBalanceMovements) {
                destinationRow[actualColIndex + 1] = getValueWithDenominator((sourceCol.totalValueCredit ?? 0) - (sourceCol.totalValueInternalCredit ?? 0));
                destinationRow[actualColIndex + 2] = getValueWithDenominator((sourceCol.totalValueDebit ?? 0) - (sourceCol.totalValueInternalDebit ?? 0));

              } else {

                destinationRow[actualColIndex + 1] = getValueWithDenominator(sourceCol.totalValueCredit ?? 0);
                destinationRow[actualColIndex + 2] = getValueWithDenominator(sourceCol.totalValueDebit ?? 0);
              }

              destinationRow[actualColIndex + 3] = getValueWithDenominator(sourceCol.totalValue);


            } else {
              actualColIndex = (lastReportColIndex) + 2 + +colIndex;

              destinationRow[actualColIndex + 1] = getValueWithDenominator(sourceCol.totalValue);

            }
          }


          if (this.showTurnover) {
            destinationRow[actualColIndex + 4] = getValueWithDenominator(this.sumBalances[+rowIndex].sum ?? 34);
            destinationRow[actualColIndex + 5] = getValueWithDenominator(this.sumBalances[+rowIndex].onlyBalancesSum);
          } else {
            destinationRow[actualColIndex + 2] = getValueWithDenominator(this.sumBalances[+rowIndex].sum);
            destinationRow[actualColIndex + 3] = getValueWithDenominator(this.sumBalances[+rowIndex].onlyBalancesSum);
          }


        }
        return finalArray;
      }


      const headersColumns = createHeadersColumns();

      const exportedData = createExportedData();

      excelExport(headersColumns, exportedData, {
        tableName: 'CF_report',
        sheetName: `${this.actualDefinition.name}(${this.actualDefinition.reportCurrency})`,
        decimalPLaces: this.getPreference('cashFlowDecimalPlaces')
      })
    }
  },
  mounted() {
    this.actualDefinition = this.$_.cloneDeep(this.reportDefinition(this.reportId));
    this.actualDefinition.denominator = `${this.actualDefinition.denominator}`;
    // this.generate();
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/global";

.cashflow-table {
  //table-layout: fixed;
  position: relative;
  width: 100%;
  color: var(--table-body-font-color);
  /*border-collapse: collapse;*/
  border-style: hidden;
  background-color: var(--table-body-color);
  border-spacing: 0;
}

$height: 28px;

.cashflow-table thead > tr > th {
  position: sticky;
  border-bottom: 1px solid var(--table-border-color);
  background-color: var(--table-header-color);
  color: var(--table-header-font-color);
  font-weight: normal;
  padding: 5px 4px 5px 4px;
  z-index: 10;
  text-align: right;
  border-right: 1px solid var(--table-border-color);
}


.cashflow-table-caption {
  text-align: left;
}

.cashflow-table > tbody > tr > td {
  cursor: default;
  border-bottom: 1px solid var(--table-border-color);
  border-right: 1px solid var(--table-border-color);
  padding: 2px 8px 2px 8px;
}

.value-cell {
  text-align: right;
  white-space: nowrap;
}

.sum-header {
  background-color: mix($navigationGray, $openRed, 70) !important;
}

.sum-cell {
  background-color: mix($navigationGray, $openRed, 50) !important;
}

$drillDownBorderColor: var(--table-border-color);


.drill-down-cell {
  &-single {
    border-left: 3px solid $drillDownBorderColor !important;
    border-right: 3px solid $drillDownBorderColor !important;
    border-top: 3px solid $drillDownBorderColor !important;
    border-bottom: none !important;
    background-color: var(--table-body-color);
  }

  &-right {
    border-right: 3px solid $drillDownBorderColor !important;
    border-top: 3px solid $drillDownBorderColor !important;
    border-bottom: none !important;
    background-color: var(--table-body-color);
  }

  &-left {
    border-left: 3px solid $drillDownBorderColor !important;
    border-top: 3px solid $drillDownBorderColor !important;
    border-bottom: none !important;
    background-color: var(--table-body-color);
  }

  &-middle {
    border-top: 3px solid $drillDownBorderColor !important;
    border-bottom: none !important;
    background-color: var(--table-body-color);
  }
}

.drill-down-detail-cell {
  border-top: none !important;
  border-left: 3px solid $drillDownBorderColor !important;
  border-right: 3px solid $drillDownBorderColor !important;
  border-bottom: 3px solid $drillDownBorderColor !important;
  background-color: var(--table-body-color);
  box-shadow: black 20px 20px;
}

.drill-down-left-border {
  border-top: 3px solid $drillDownBorderColor !important;
  border-left: 3px solid $drillDownBorderColor !important;
  border-right: none !important;
  border-bottom: none !important;
  background-color: var(--table-body-color);
  font-size: 2px;
}

.drill-down-middle-border {
  border: none !important;
  background-color: var(--table-body-color);
  font-size: 2px;
}

.drill-down-right-border {
  border-top: 3px solid $drillDownBorderColor !important;
  border-right: 3px solid $drillDownBorderColor !important;
  border-left: none !important;
  border-bottom: none !important;
  background-color: var(--table-body-color);
  font-size: 2px;
}

.dd-currency-container {
  display: flex;
  flex-flow: row;
}

.dd-currency-box {
  background-color: $navigationGray;
  padding: 10px 10px;
  width: 120px;
  text-align: center;
  margin: 5px 5px;
}

.actual-date-row > td {
  background-color: $openRed;
  color: white;
}

.break-date-row > td {
  border-top: 2px dotted lighten($openRed, 10) !important;
}

.date-cell, .date-header {
  position: sticky;
  /*position:absolute;*/
  /*width: 4vw;*/
  left: 0;
  top: auto;
  /*top: 0;*/
  left: -20px;
  background-color: var(--card-background-color);
}

.date-header {
  z-index: 15 !important;
}


.weekend-date-row > td {
  background-color: mix($backgroundColor, white, 85);
}

.split-row {
  background-color: var(--table-border-color);
  height: 2em;
  align-content: center;
  align-items: center;
  text-align: center;
}

.start-of-month-row {
  border-top: 1px solid var(--table-border-color) !important;
  background: linear-gradient(0deg, transparent 0%, transparentize(#262C32, 0.3) 100%);
}

.month-row {
  background-color: var(--table-border-color);
  text-align: center;
}

</style>
